import { z } from 'zod';
export const UsageQuerySchema = z.object({
    agent_id: z.string().uuid().optional(),
    customer_id: z.string().uuid().optional(),
    from_date: z.string().datetime().optional(),
    to_date: z.string().datetime().optional(),
    event_name: z.string().optional(),
    granularity: z
        .enum(['hour', 'day', 'week', 'month'])
        .default('day')
        .optional(),
});
export const CurrentUsageResponseSchema = z.object({
    agent_id: z.string().uuid().nullable(),
    customer_id: z.string().uuid().nullable(),
    period_start: z.string().datetime(),
    period_end: z.string().datetime(),
    events: z.array(z.object({
        event_name: z.string(),
        count: z.number(),
        total_value: z.number().nullable(),
        avg_value: z.number().nullable(),
        min_value: z.number().nullable(),
        max_value: z.number().nullable(),
    })),
    total_events: z.number(),
    total_cost_usd: z.number().nullable(),
});
export const UsageAggregatesQuerySchema = z.object({
    agent_id: z.string().uuid().optional(),
    customer_id: z.string().uuid().optional(),
    from_date: z.string().datetime(),
    to_date: z.string().datetime(),
    group_by: z
        .array(z.enum([
        'agent_id',
        'customer_id',
        'event_name',
        'hour',
        'day',
        'week',
        'month',
    ]))
        .optional(),
    metrics: z
        .array(z.enum(['count', 'sum', 'avg', 'min', 'max', 'unique_sessions']))
        .default(['count'])
        .optional(),
});
export const UsageAggregatesResponseSchema = z.object({
    period: z.object({
        from: z.string().datetime(),
        to: z.string().datetime(),
    }),
    aggregates: z.array(z.object({
        dimensions: z.record(z.string(), z.union([z.string(), z.number(), z.null()])),
        metrics: z.object({
            count: z.number().optional(),
            sum: z.number().optional(),
            avg: z.number().optional(),
            min: z.number().optional(),
            max: z.number().optional(),
            unique_sessions: z.number().optional(),
        }),
        period: z.string().datetime().optional(),
    })),
    total_records: z.number(),
});
export const CustomUsageQuerySchema = z.object({
    query: z.object({
        filters: z
            .object({
            agent_id: z.array(z.string().uuid()).optional(),
            customer_id: z.array(z.string().uuid()).optional(),
            event_name: z.array(z.string()).optional(),
            date_range: z
                .object({
                from: z.string().datetime(),
                to: z.string().datetime(),
            })
                .optional(),
            properties: z
                .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]))
                .optional(),
            value_range: z
                .object({
                min: z.number().optional(),
                max: z.number().optional(),
            })
                .optional(),
        })
            .optional(),
        group_by: z.array(z.string()).optional(),
        select: z.array(z.object({
            field: z.string(),
            aggregation: z.enum(['count', 'sum', 'avg', 'min', 'max', 'distinct']),
            alias: z.string().optional(),
        })),
    }),
    limit: z.number().int().min(1).max(1000).default(100).optional(),
    offset: z.number().int().min(0).default(0).optional(),
});
export const LiveMetricsQuerySchema = z.object({
    agent_id: z.string().uuid().optional(),
    customer_id: z.string().uuid().optional(),
    event_name: z.string().optional(),
    window_minutes: z.number().int().min(1).max(1440).default(60).optional(),
});
export const LiveMetricsResponseSchema = z.object({
    timestamp: z.string().datetime(),
    window_start: z.string().datetime(),
    window_end: z.string().datetime(),
    metrics: z.object({
        events_per_minute: z.number(),
        total_events: z.number(),
        unique_sessions: z.number(),
        unique_customers: z.number(),
        avg_event_value: z.number().nullable(),
        total_cost_usd: z.number().nullable(),
    }),
    events_by_name: z.array(z.object({
        event_name: z.string(),
        count: z.number(),
        percentage: z.number(),
    })),
});
export const RecalculateUsageSchema = z.object({
    agent_id: z.string().uuid().optional(),
    customer_id: z.string().uuid().optional(),
    from_date: z.string().datetime(),
    to_date: z.string().datetime(),
    force: z.boolean().default(false).optional(),
});
export const RecalculateUsageResponseSchema = z.object({
    job_id: z.string(),
    status: z.literal('started'),
    estimated_duration_minutes: z.number(),
    records_to_process: z.number(),
});
export const CustomerUsageQuerySchema = z.object({
    from_date: z.string().datetime().optional(),
    to_date: z.string().datetime().optional(),
    days: z.coerce.number().int().min(1).max(365).default(30).optional(),
    agent_id: z.string().optional(),
});
export const CustomerAgentUsageResponseSchema = z.object({
    agents: z.array(z.object({
        agent_id: z.string(),
        agent_name: z.string(),
        tasks_completed: z.number(),
        workflows_completed: z.number(),
        outcomes_achieved: z.number(),
        total_cost: z.number(),
        total_revenue: z.number(),
        last_activity: z.string().nullable(),
        signals: z.array(z.object({
            signal_id: z.string(),
            signal_name: z.string(),
            signal_kind: z.enum(['task', 'workflow', 'outcome']),
            event_count: z.number(),
            total_value: z.number(),
            unit_price: z.number(),
            cost: z.number(),
        })),
    })),
});
export const CustomerDailyUsageResponseSchema = z.object({
    daily: z.array(z.object({
        date: z.string(),
        tasks: z.number(),
        workflows: z.number(),
        outcomes: z.number(),
        cost: z.number(),
        revenue: z.number(),
    })),
});
//# sourceMappingURL=index.js.map